import moment from 'moment';

import { WorkerType } from '@/modules/worker/types';
import {
	Availabilities,
	DistanceValue,
	LastUpdateValue,
} from '@/types/filters';

import type { ActiveFilter } from '@/dsComponents/FilterDropdown/types';
import type { PeriodPreference } from '@/modules/companyNeed/types/periodPreference';
import type { EContractType } from '@/types/contract';
import type { JobExperience } from '@/types/job';

export enum CompanyNeedFilterCategoryId {
	QUALIFICATION = 'qualification',
	EXCLUDED_QUALIFICATION = 'excludedQualification',
	HABILITATIONS = 'habilitations',
	DISTANCE = 'distance',
	AVAILABILITIES = 'availabilities',
	LAST_UPDATE = 'lastUpdate',
	EXPERIENCE = 'experience',
	CONTRACT_TYPE = 'contractType',
	ORIGIN = 'origin',
	KEYWORDS = 'keywords',
	CITIES = 'cities',
	WORKER_TYPE = 'workerType',
	ONGOING_PROPOSAL = 'ongoingProposal',
}

const findFilter = (
	filters: ActiveFilter[],
	categoryId: CompanyNeedFilterCategoryId
) => filters.find((f) => f.categoryId === categoryId);

export const extractDistanceKm = (
	filters: ActiveFilter[]
): number | undefined => {
	const selectedValue = findFilter(
		filters,
		CompanyNeedFilterCategoryId.DISTANCE
	)?.selectedOptions?.[0]?.value;
	if (!selectedValue || selectedValue === DistanceValue.ANY) return undefined;
	return Number(selectedValue);
};

export const extractLastUpdate = (
	filters: ActiveFilter[]
): string | undefined => {
	const selectedValue = findFilter(
		filters,
		CompanyNeedFilterCategoryId.LAST_UPDATE
	)?.selectedOptions?.[0]?.value;
	if (!selectedValue) return undefined;

	switch (selectedValue) {
		case LastUpdateValue.WEEK:
			return moment().subtract(7, 'days').toISOString();
		case LastUpdateValue.MONTH:
			return moment().subtract(30, 'days').toISOString();
		default:
			return undefined;
	}
};

export const extractContractTypes = (
	filters: ActiveFilter[]
): EContractType[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.CONTRACT_TYPE
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions
		.map((option) => option.value)
		.filter((value): value is EContractType => !!value);
};

export const extractExperienceLevels = (
	filters: ActiveFilter[]
): JobExperience[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.EXPERIENCE
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions
		.map((option) => option.value)
		.filter((value): value is JobExperience => !!value);
};

export const extractQualification = (
	filters: ActiveFilter[]
): string[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.QUALIFICATION
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions.map((option) => option.value);
};

export const extractExcludedQualification = (
	filters: ActiveFilter[]
): string[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.EXCLUDED_QUALIFICATION
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions.map((option) => option.value);
};

export const extractHabilitations = (
	filters: ActiveFilter[]
): string[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.HABILITATIONS
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	const values = selectedOptions
		.map((option) => option.value)
		.filter(
			(value): value is string => typeof value === 'string' && value.length > 0
		);
	return values.length === 0 ? undefined : values;
};

export const extractAvailabilities = (
	filters: ActiveFilter[]
): PeriodPreference[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.AVAILABILITIES
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions
		.map((option) => option.value)
		.filter((value) => value !== Availabilities.WEEKEND);
};

export const extractWeekEndRequired = (
	filters: ActiveFilter[]
): boolean | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.AVAILABILITIES
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions.some(
		(option) => option.value === Availabilities.WEEKEND
	);
};

export const extractOrigins = (
	filters: ActiveFilter[]
): string[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.ORIGIN
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	return selectedOptions.map((option) => option.value);
};

export const extractKeywords = (
	filters: ActiveFilter[]
): string[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.KEYWORDS
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	const values = selectedOptions
		.map((option) => option.value)
		.filter(
			(value): value is string => typeof value === 'string' && value.length > 0
		);
	return values.length === 0 ? undefined : values;
};

export const extractCities = (
	filters: ActiveFilter[]
): string[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.CITIES
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	const values = selectedOptions
		.map((option) => option.value)
		.filter(
			(value): value is string => typeof value === 'string' && value.length > 0
		);
	return values.length === 0 ? undefined : values;
};

export const extractWorkerTypes = (
	filters: ActiveFilter[]
): WorkerType[] | undefined => {
	const selectedOptions = findFilter(
		filters,
		CompanyNeedFilterCategoryId.WORKER_TYPE
	)?.selectedOptions;
	if (!selectedOptions || selectedOptions.length === 0) return undefined;
	const values = selectedOptions
		.map((option) => option.value)
		.filter(
			(value): value is WorkerType =>
				value === WorkerType.CANDIDATE || value === WorkerType.INTERIM
		);
	return values.length === 0 ? undefined : values;
};

export const extractHasOngoingProposal = (
	filters: ActiveFilter[]
): boolean | undefined => {
	const selectedValue = findFilter(
		filters,
		CompanyNeedFilterCategoryId.ONGOING_PROPOSAL
	)?.selectedOptions?.[0]?.value;
	if (selectedValue === undefined) return undefined;
	return selectedValue === 'true';
};
